import { useEffect, useMemo, useState } from 'react';
import { useImportJobsStore } from '../../../application/store/importJobsStore';
import { TERMINAL_IMPORT_JOB_STATES } from '@domain/connection/models/ImportJob';
import { useImportJobs, TrackedImportJob } from './useImportJobs';
import { formatImportTarget, useActiveImportJobs } from './useActiveImportJobs';

export const isTerminalImportJob = (job: TrackedImportJob) =>
  !!job.status && TERMINAL_IMPORT_JOB_STATES.includes(job.status.state);

/**
 * Tracks import jobs for a connection (or all connections when `connectionId` is omitted).
 * Seeds the global store from active jobs and keeps finished jobs visible until unmount.
 */
export const useConnectionImportJobs = (connectionId?: string) => {
  const storeJobs = useImportJobsStore((s) => s.jobs);
  const addJob = useImportJobsStore((s) => s.addJob);
  const { jobs } = useImportJobs();
  const { jobs: activeJobs, isLoading, isFetched } = useActiveImportJobs(connectionId);
  const [trackedJobIds, setTrackedJobIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!activeJobs.length) return;

    setTrackedJobIds((prev) => {
      const next = new Set(prev);
      activeJobs.forEach((job) => next.add(job.jobId));
      return next;
    });

    activeJobs.forEach((job) => {
      // Keep labels set by the sync action (e.g. "Store · Products").
      if (storeJobs.some((tracked) => tracked.id === job.jobId)) return;
      addJob({
        id: job.jobId,
        connector: job.connector,
        label: formatImportTarget(job.target),
        startedAt: job.startedAt,
      });
    });
  }, [activeJobs, addJob, storeJobs]);

  const displayJobs = useMemo(() => {
    const byId = new Map<string, TrackedImportJob>();

    jobs
      .filter((job) => trackedJobIds.has(job.id))
      .forEach((job) => byId.set(job.id, job));

    activeJobs.forEach((job) => {
      const existing = byId.get(job.jobId);
      byId.set(job.jobId, {
        id: job.jobId,
        connector: job.connector,
        label: existing?.label ?? formatImportTarget(job.target),
        startedAt: job.startedAt,
        status: { state: job.state, progress: job.progress, result: job.result },
        isLoading: false,
      });
    });

    return Array.from(byId.values()).sort((a, b) => b.startedAt - a.startedAt);
  }, [jobs, activeJobs, trackedJobIds]);

  return {
    displayJobs,
    activeJobs,
    isLoading,
    isFetched,
  };
};
